import { FC, MouseEvent, useState } from 'react';
import {
  ActivityCard,
  ActivityCardBadge,
  InPersonActivityTag,
  OnlineActivityTag,
} from '@care/provider-react-components';

import { ActivityParticipationType, SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { activityAgesToRangeText } from '@/utils/activityAgesToRangeText';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_activitySearchResults_seoSegmentationActivity as ActivityType } from '@/__generated__/seoSegmentationListingPage';
import { seoActivitySegmentationListingPage_seoSegmentationActivityDetails_SEOSegmentationActivityDetailsSuccess_segmentDetails as SEOActivityDetails } from '@/__generated__/seoActivitySegmentationListingPage';
import { useResponsive } from '@/components/hooks/useResponsive';
import {
  RedirectingBackdrop,
  RedirectingBackdropProps,
} from '@/components/page/RedirectingBackdrop';
import {
  getActivityCardImages,
  getActivityCardProvider,
  getActivityCardRating,
  getActivityCardUrl,
} from './helpers';

interface ActivityCardItemProps {
  activity: ActivityType;
  activityDetails: SEOActivityDetails;
}

const BadgeToComponent: Record<ActivityParticipationType, FC> = {
  IN_PERSON: InPersonActivityTag,
  ONLINE: OnlineActivityTag,
};

export const ActivityCardListItem: FC<ActivityCardItemProps> = ({ activity, activityDetails }) => {
  const { isSmallViewport } = useResponsive();
  const [redirectingTo, setRedirectingTo] = useState<RedirectingBackdropProps['to']>(null);
  const getTags = () => {
    const tags = [];
    if (activity.participationType) {
      const BadgeComponent = BadgeToComponent[activity.participationType];
      tags.push(<BadgeComponent />);
    }
    const ageText = activityAgesToRangeText(activity.minAgeMonths, activity.maxAgeMonths);
    if (ageText) {
      tags.push(<ActivityCardBadge variant="tag" text={ageText} />);
    }

    return tags;
  };

  const currentPrice =
    Number(activity.discount?.price?.amount) > 0
      ? Number(activity.discount?.price?.amount)
      : Number(activity.price?.amount);
  const previousPrice =
    Number(activity.discount?.price?.amount) > 0
      ? Number(activity.price?.amount)
      : Number(activity.discount?.price?.amount);

  const getDiscount = () => {
    if (!activity.discount?.description) return null;
    return <ActivityCardBadge text={activity.discount?.description} />;
  };

  const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const provider = activity.externalVendor?.vendorName;
    const externalUrl = activity.externalVendor?.info?.activityUrl ?? null;

    const hasExternalUrl = externalUrl && provider;

    if (isSmallViewport) {
      setRedirectingTo(hasExternalUrl ? provider : 'internal');
    }

    if (hasExternalUrl && !isSmallViewport) {
      e.preventDefault();
      const redirectParams = new URLSearchParams({
        provider,
        to: externalUrl,
      });

      trackAmplitudeEvent(AmplitudeEvent.viatorCardClick, {
        feature_name: 'Viator Activity Clicked',
        url: globalThis.location.href,
        url_path: globalThis.location.pathname,
        viator_url: externalUrl,
        viatorActivityPrice: currentPrice,
        seo_page_type: 'C&A SEO page',
        page_template:
          activityDetails.pageType === SEOSegmentationPageType.NATIONAL ? 'National' : 'Local',
        seo_page_title: activityDetails?.headline ?? '',
        service_id_for_member: `${activityDetails?.serviceIdForMember}Activity`,
      });
      window.open(`./redirect?${redirectParams.toString()}`, '_blank', 'noopener,noreferrer');
    }
  };

  const onReadMoreClick = () => {
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      feature_name: 'Read More Description',
    });
  };

  return (
    <>
      <ActivityCard
        badge={getDiscount()}
        title={activity.title}
        imageUrls={getActivityCardImages(activity)}
        organizatorName={getActivityCardProvider(activity)}
        description={activity.description ?? ''}
        price={currentPrice === 0 ? undefined : currentPrice}
        statusText={currentPrice === 0 ? 'Check price' : undefined}
        statusTextSx={({ palette }) => ({
          textDecoration: 'underline',
          fontWeight: 400,
          color: palette.text.tertiary,
        })}
        prevPrice={
          previousPrice && !activity.discount?.description?.toLowerCase().includes('sibling')
            ? previousPrice
            : undefined
        }
        tags={getTags()}
        rating={getActivityCardRating(activity)}
        locationName={activity.address?.cityState ?? ''}
        linkProps={{
          href: getActivityCardUrl(activity),
          target: isSmallViewport ? '' : '_blank',
          onClick: handleLinkClick,
        }}
        hideFavoriteIcon
        onReadMoreClick={onReadMoreClick}
      />
      <RedirectingBackdrop to={redirectingTo} />
    </>
  );
};
